import { useCallback, useContext, useEffect, useState, useMemo } from "react";
import { useSelector } from "react-redux";
import { useModal } from "@stadium/hooks/lib/use-modal";
import {
  makeFilterSort,
  selectActiveColorOptionValueIds,
  selectActiveSizeOptionValueIds,
  selectActiveDietaryRestrictionsIds,
  selectActiveBrandIds,
  selectActivePriceRangeIds,
} from "@stadium/redux-utils/selectors/filterSelector";
import isEmpty from "lodash/isEmpty";
import debounce from "lodash/debounce";

import Product from "@sharedComponents/cards/product";
import { ProductListingContext } from "@components/store/products/listing/listing-provider";
import ProductListingHeader from "@components/store/products/listing-header";
import ProductDetailsModal from "@components/store/product-details-modal";
import { usePreviewModeContext } from "@contexts/preview-mode-context";
import ByoBoxIsland from "@components/store/product-detail/byo/byo-box-island";
import Portal from "@stadium/ui/src/components/portal";
import OldAvailableProductsList from "@components/store/product-detail/byo/available-products-list/old-product-list";

import useSearchProductList from "@hooks/search/use-fetch-base-product-list";
import useFilterConfig from "@hooks/search/use-filter-config";
import { useFilter } from "@stadium/shared/src/hooks/use-filter";
import { useStoreContext } from "@components/store/provider";

import { handleQuantityOfProductInBox, removeProduct } from "@helpers/orders/byo-box";
import { getCanBuyProduct } from "@helpers/product-details";

import { getCountry } from "@store/selectors/country";
import {
  getStoreID,
  getStoreParentId,
  getCommonElasticSearchStoreVariables,
} from "@store/selectors/store";

import { PRODUCT_DETAILS_MODAL } from "@constants/modal-types";
import { LISTING_SECTION_REF } from "@components/store/products/listing";
import {
  ITEM_FEATURES_FILTER_KEY,
  ITEM_TYPE_FILTER_KEY,
  MENU_CATEGORY_FILTER_KEY,
  PRODUCTION_DAYS_FILTER_KEY,
} from "@constants/products/listing";

import styles from "./main.module.scss";

const DEFAULT_PAGE = 1;
const PER_PAGE = 150;
const FILTER_KEYS_TO_HIDE = [
  MENU_CATEGORY_FILTER_KEY,
  PRODUCTION_DAYS_FILTER_KEY,
  ITEM_TYPE_FILTER_KEY,
  ITEM_FEATURES_FILTER_KEY,
];

const AvailableProductsList = ({ product }) => {
  const [selectedOpenProduct, setSelectedOpenProduct] = useState();
  const { openModal: openProductDetailsModal, renderModal: renderProductDetailsModal } = useModal(
    PRODUCT_DETAILS_MODAL,
    ProductDetailsModal,
  );

  const { boxProductList, handleBoxProductList } = useContext(ProductListingContext);

  const { data: filterState, init: initFilter, clear: clearFilters } = useFilter();
  const activeColorOptionValueIds = useSelector(selectActiveColorOptionValueIds);
  const activeSizeOptionValueIds = useSelector(selectActiveSizeOptionValueIds);
  const activeSortBy = useSelector(useMemo(() => makeFilterSort(), []));
  const activePriceRangeIds = useSelector(selectActivePriceRangeIds);
  const activeDietaryRestrictionsIds = useSelector(selectActiveDietaryRestrictionsIds);
  const activeFlattenedBrandValueIds = useSelector(selectActiveBrandIds);

  const { getStoreUpdatedAt } = usePreviewModeContext();

  const commonESVariables = useSelector(getCommonElasticSearchStoreVariables);

  const storeParentId = useSelector(getStoreParentId);
  const storeId = useSelector(getStoreID);
  const country = useSelector(getCountry);
  const { componentProductIds } = product;

  const canBuyParentProduct = getCanBuyProduct({ product });

  const getVariables = useCallback(() => {
    const variables = {
      storeId: storeParentId,
      designedStoreIds: [storeId],
      countryCode: country.iso?.toLowerCase(),
      taxonIds: product.enabledTaxonIds.map((id) => +id),
      perPage: PER_PAGE,
      sortBy: activeSortBy,
      dietaryRestrictionIds: activeDietaryRestrictionsIds?.map((id: string) => +id) || [],
      priceKeys: activePriceRangeIds,
      brandIds: activeFlattenedBrandValueIds,
      color: activeColorOptionValueIds,
      size: activeSizeOptionValueIds,
      preFilterProductIds: componentProductIds.map((id) => +id),
      storeUpdatedAt: getStoreUpdatedAt(),
      excludeOnlyBundleComponentItems: false,
      includeProductVariants: true,
      ...commonESVariables,
    };

    return variables;
  }, [
    storeParentId,
    country,
    activeColorOptionValueIds,
    activeSizeOptionValueIds,
    activeSortBy,
    activePriceRangeIds,
    activeDietaryRestrictionsIds,
    componentProductIds,
    activeFlattenedBrandValueIds,
    getStoreUpdatedAt,
    commonESVariables,
  ]);

  const {
    fetchedProducts,
    totalProducts,
    getProductList,
    loading: isProductsLoading,
    aggregations,
  } = useSearchProductList();

  const debouncedGetProductList = useMemo(() => debounce(getProductList, 500), [getProductList]);

  const loading = isProductsLoading || isEmpty(filterState);

  const filterConfig = useFilterConfig({
    aggregations,
    hideFilterKeys: FILTER_KEYS_TO_HIDE,
  });

  useEffect(() => {
    debouncedGetProductList({
      variables: getVariables(),
    });
    return () => {
      debouncedGetProductList.cancel();
    };
  }, [
    commonESVariables,
    getVariables,
    country,
    activePriceRangeIds,
    activeDietaryRestrictionsIds,
    activeColorOptionValueIds,
    activeSizeOptionValueIds,
    activeSortBy,
  ]);

  useEffect(() => {
    initFilter();
    return () => clearFilters([]);
  }, []);

  const handleOpenDetailsModal = (id) => {
    setSelectedOpenProduct(id);
    openProductDetailsModal();
  };

  const addToBox = ({ newQuantity, product, externalId }) => {
    handleQuantityOfProductInBox({
      externalId,
      newQuantity,
      product,
      boxProductList,
      handleBoxProductList,
    });
  };

  const removeFromBox = (externalId) => {
    removeProduct({ externalId, boxProductList, handleBoxProductList });
  };

  const renderProducts = () => {
    return (
      <article className={styles.rowWrapper}>
        {fetchedProducts.map((product) => (
          <div key={`product-${product.id}`} className={styles.productCard}>
            <Product
              data={product}
              key={product.id}
              source="search"
              productTileClass={styles.productTileBYO}
              productImageClass={styles.productImageBlock}
              productBrandClass={styles.productBrand}
              productNameClass={styles.productName}
              productPriceClass={styles.productPrice}
              productBottomRowClass={styles.productBottomRow}
              productTileFooterClass={styles.tileFooter}
              productQtyBlockClass={styles.qtyBlock}
              className={styles.productCardContainer}
              openProductDetailsModal={handleOpenDetailsModal}
              showAddToBox
              addToBox={addToBox}
              removeFromBox={removeFromBox}
              disabled={!canBuyParentProduct}
            />
          </div>
        ))}
      </article>
    );
  };

  return (
    <>
      <section ref={LISTING_SECTION_REF}>
        <article>
          <section className="t-bg-4">
            <div className={`${styles.pageContainer} ${styles.byoProductListing}`}>
              <ProductListingHeader
                totalProducts={totalProducts}
                currentPage={DEFAULT_PAGE}
                perPage={PER_PAGE}
                showSortDropdown
                showFilters
                isFetchingProducts={loading}
                filterConfig={filterConfig}
              />
              {renderProducts()}
              {renderProductDetailsModal({
                classes: {
                  rootClass: styles.productDetailsPrimaryModal,
                  dialogClass: styles.modalDialogProductDetails,
                },
                data: {
                  productData: fetchedProducts.find(({ id }) => id === selectedOpenProduct),
                  viewingInsideByoMedley: true,
                  showAddToBox: true,
                  showFooter: false,
                  canBuyParentProduct,
                },
              })}
            </div>
          </section>
        </article>
      </section>
      <Portal
        dataContainerId="byo-box-island-portal"
        id="byo-box-island-container"
        className={styles.byoBoxIslandPortal}
      >
        <ByoBoxIsland fetchedProducts={fetchedProducts} product={product} />
        <Portal
          dataContainerId="extra-spacing-for-byo-portal"
          id="byo-box-island-container"
          className={`${styles.extraSpacingForBYO} extraSpacingForBYO t-bg-4`}
        ></Portal>
      </Portal>
    </>
  );
};

const AvailableProductsListWrapper = (props) => {
  const { isNewSwagmagicHost } = useStoreContext();
  return isNewSwagmagicHost ? (
    <AvailableProductsList {...props} />
  ) : (
    <OldAvailableProductsList {...props} />
  );
};

export default AvailableProductsListWrapper;
